import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Loader2, FileText, Copy, Trash2, ReceiptText,
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { useDebounced, useLatestRequest } from '../../../lib/searchHooks';
import {
  fmtINR, formatErr, notifyErr, inputCls, StatusBadge, todayISO,
} from '../../../components/admin/billing/shared';

const fmtValid = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso.slice(0, 10));
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch {
    return iso;
  }
};

// Returns { text, cls, testid } for the "Valid until" pill. `expiring` when
// within the next 2 days, `expired` when in the past, else neutral.
const validityPill = (iso) => {
  if (!iso) return null;
  const today = todayISO();
  const target = iso.slice(0, 10);
  if (target < today) {
    return { text: `Expired ${fmtValid(target)}`, cls: 'bg-red-50 text-red-700 border-red-200', testid: 'validity-expired' };
  }
  // ISO-string comparison works for YYYY-MM-DD; compute delta in days for the
  // "expiring soon" window without touching Date math edge cases.
  const t = new Date(target);
  const n = new Date(today);
  const days = Math.round((t - n) / 86400000);
  if (days <= 2) {
    return { text: `Valid until ${fmtValid(target)}`, cls: 'bg-amber-50 text-amber-800 border-amber-200', testid: 'validity-expiring' };
  }
  return { text: `Valid until ${fmtValid(target)}`, cls: 'bg-slate-50 text-slate-600 border-slate-200', testid: 'validity-ok' };
};

const BillingList = ({ type }) => {
  // `type` is "estimate" or "invoice"
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [convertingId, setConvertingId] = useState('');

  const plural = type === 'estimate' ? 'estimates' : 'invoices';
  const Title = type === 'estimate' ? 'Estimates' : 'Invoices';

  const qDeb = useDebounced(q);
  const nextReq = useLatestRequest();
  const fetchAll = useCallback(async () => {
    setLoading(true);
    const live = nextReq();
    try {
      const params = {};
      if (qDeb.trim()) params.q = qDeb.trim();
      if (statusFilter) params.status = statusFilter;
      const { data } = await axios.get(`${API}/billing/${plural}`, {
        headers: authHeader(), params,
      });
      if (live()) setList(data);
    } catch (e) {
      if (live()) setErr(formatErr(e));
    } finally {
      if (live()) setLoading(false);
    }
  }, [authHeader, qDeb, plural, statusFilter, nextReq]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onClone = async (id) => {
    if (type !== 'estimate') return;
    try {
      const { data } = await axios.post(`${API}/billing/estimates/${id}/clone`, {}, { headers: authHeader() });
      navigate(`/admin/billing/estimates/${data.id}`);
    } catch (e) { notifyErr(e); }
  };

  // Estimates are GST-only now — convert straight to a GST tax invoice with
  // sensible defaults. No modal required.
  const onConvert = async (doc) => {
    if (type !== 'estimate') return;
    if (!window.confirm(
      `Convert ${doc.number} to a new GST Tax Invoice?\n\n`
      + '• A draft GST invoice will be created with a fresh UNS/GST/<FY>/<seq> number\n'
      + '• Line items carry over with default HSN 998719 + 18% GST (edit before saving)\n'
      + '• The estimate itself is NOT modified',
    )) return;
    setConvertingId(doc.id);
    try {
      const { data } = await axios.post(
        `${API}/gst/invoices/from-estimate/${doc.id}`,
        { default_hsn: '998719', default_gst_rate: 18, treat_rates_as: 'exclusive' },
        { headers: authHeader() },
      );
      navigate(`/admin/gst/invoices/${data.id}`);
    } catch (e) { notifyErr(e); }
    finally { setConvertingId(''); }
  };

  const onDelete = async (doc) => {
    if (!window.confirm(`Delete ${type} ${doc.number}?`)) return;
    try {
      await axios.delete(`${API}/billing/${plural}/${doc.id}`, { headers: authHeader() });
      fetchAll();
    } catch (e) { notifyErr(e); }
  };

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-slate-900">{Title}</h1>
            <p className="text-sm text-slate-500">
              {type === 'estimate'
                ? 'Create GST estimates, clone them, and convert to GST tax invoices when accepted.'
                : 'Track invoices, payments, and overdue balances.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate(`/admin/billing/${plural}/new`)}
            disabled={Boolean(err)}
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm"
            data-testid={`new-${type}-btn`}
          >
            <Plus size={16} /> New {type === 'estimate' ? 'Estimate' : 'Invoice'}
          </button>
        </div>

        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${plural} by reg. number, customer, number, or subject`}
            className={`${inputCls} pl-9`}
            data-testid={`${type}-search`}
          />
        </div>

        {/* Status filter chips */}
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="status-filter-chips">
          {(type === 'invoice'
            ? [
                { v: '', label: 'All', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
                { v: 'draft', label: 'Draft', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
                { v: 'sent', label: 'Sent', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
                { v: 'partially_paid', label: 'Partially Paid', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
                { v: 'paid', label: 'Paid', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
                { v: 'overdue', label: 'Overdue', cls: 'bg-red-100 text-red-700 border-red-300' },
                { v: 'cancelled', label: 'Cancelled', cls: 'bg-slate-100 text-slate-500 border-slate-300' },
              ]
            : [
                { v: '', label: 'All', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
                { v: 'draft', label: 'Draft', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
                { v: 'sent', label: 'Sent', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
                { v: 'accepted', label: 'Accepted', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
                { v: 'converted', label: 'Converted', cls: 'bg-violet-100 text-violet-700 border-violet-300' },
                { v: 'expired', label: 'Expired', cls: 'bg-orange-100 text-orange-700 border-orange-300' },
                { v: 'cancelled', label: 'Cancelled', cls: 'bg-slate-100 text-slate-500 border-slate-300' },
              ]
          ).map((opt) => {
            const active = statusFilter === opt.v;
            return (
              <button
                key={opt.v || 'all'}
                type="button"
                onClick={() => setStatusFilter(opt.v)}
                className={`px-3 py-1 text-xs font-medium rounded-full border transition ${active ? `${opt.cls} ring-2 ring-offset-1 ring-teal-400` : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}
                data-testid={`status-chip-${opt.v || 'all'}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 mb-4">{err}</div>}

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="text-center text-slate-500 py-12"><Loader2 className="animate-spin inline mr-2" size={18} /> Loading…</div>
          ) : err ? (
            <div className="text-center text-slate-500 py-12" data-testid={`${type}-blocked`}>
              This list could not be loaded.
            </div>
          ) : list.length === 0 ? (
            <div className="text-center text-slate-500 py-12">
              No {plural} yet. Click <b>New {type === 'estimate' ? 'Estimate' : 'Invoice'}</b> to create one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={`${type}-table`}>
                <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Number</th>
                    <th className="text-left px-4 py-3 font-semibold">Date</th>
                    <th className="text-left px-4 py-3 font-semibold">Customer</th>
                    <th className="text-left px-4 py-3 font-semibold">Subject</th>
                    <th className="text-right px-4 py-3 font-semibold">Total</th>
                    <th className="text-left px-4 py-3 font-semibold">Status</th>
                    {type === 'estimate' && (
                      <th className="text-left px-4 py-3 font-semibold">Validity</th>
                    )}
                    <th className="text-right px-4 py-3 font-semibold w-44">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((d) => {
                    const pill = type === 'estimate' ? validityPill(d.validity_date) : null;
                    return (
                    <tr
                      key={d.id}
                      className="border-t border-slate-200 hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/admin/billing/${plural}/${d.id}`)}
                      data-testid={`${type}-row-${d.id}`}
                    >
                      <td className="px-4 py-3 font-mono text-teal-700">{d.number}</td>
                      <td className="px-4 py-3 text-slate-600">{(d.date || '').slice(0, 10) || '—'}</td>
                      <td className="px-4 py-3 text-slate-900" data-testid={`billing-cust-${d.id}`}>{[(d.customer_snapshot || {}).prefix, (d.customer_snapshot || {}).name].filter(Boolean).join(' ') || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 truncate max-w-xs">{d.subject || '—'}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">₹ {fmtINR(d.total_due)}</td>
                      <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                      {type === 'estimate' && (
                        <td className="px-4 py-3">
                          {pill ? (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-full border ${pill.cls}`}
                              data-testid={pill.testid}
                            >
                              {pill.text}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">—</span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => navigate(`/admin/billing/${plural}/${d.id}`)}
                            className="p-1.5 rounded hover:bg-slate-200 text-slate-600"
                            title="Open"
                          >
                            <FileText size={15} />
                          </button>
                          {type === 'estimate' && (
                            <>
                              <button
                                onClick={() => onClone(d.id)}
                                className="p-1.5 rounded hover:bg-slate-200 text-slate-600"
                                title="Clone"
                                data-testid={`clone-${d.id}`}
                              >
                                <Copy size={15} />
                              </button>
                              {d.status !== 'converted' && (
                                <button
                                  onClick={() => onConvert(d)}
                                  disabled={convertingId === d.id}
                                  className="p-1.5 rounded hover:bg-violet-100 text-violet-700 disabled:opacity-50"
                                  title="Convert to GST invoice"
                                  data-testid={`convert-${d.id}`}
                                >
                                  {convertingId === d.id
                                    ? <Loader2 className="animate-spin" size={15} />
                                    : <ReceiptText size={15} />}
                                </button>
                              )}
                            </>
                          )}
                          <button
                            onClick={() => onDelete(d)}
                            className="p-1.5 rounded hover:bg-red-100 text-red-600"
                            title="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingList;
